/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import cx from 'classnames'

import { Testable } from '@cred/react-testable'
import { product as spProduct, component as spContext } from '@cred/react-analytics/contexts'
import { currency, number } from '@cred/shared/formatters'
import { withStyles } from '@cred/react-theme'
import { collapsibleClick } from '@cred/react-analytics/events'
import { withSnowplowContexts } from '@cred/react-analytics/SnowplowContextManager'
import { extractNumber, isMobileWidth } from '@cred/shared/utils'

import { PURCHASE_TYPE } from 'mortgage-combined/rate-table/widget/common/constants'
import DisclosureModal from 'multi-vertical/rate-table/components/disclosure-modal'
import Tooltip from 'components/tooltip'
import { onlyMobile, exceptMobile } from 'common/visibility'
import CtaButton from 'components/cta-button'

import lenderDashboardImages from '../lender-logos'
import DisclaimerContent, { disclaimerContents } from './disclaimer-content'
import defaultStyles from './styles.module.scss'
import multiVerticalStyles from './multiVerticalStyles.module.scss'

const getFontTypesFromMultivertical = (isMultiVertical) => ({
  medium: isMultiVertical ? 'disclaimer' : 'body',
  large: isMultiVertical ? 'bodyLarge' : 'large',
})

const lendersWithDisclaimerList = []
const APT_TOOLTIP_TEXT = 'Annual Percentage Rate'
const isMobile = isMobileWidth()

const getDiscountDisplayValue = (discount, options) => {
  const { formData = {}, submission = {} } = options
  const { loanType } = formData.values || {}
  const currentSubmission = submission[loanType]
  const { downPayment, estimatedValue, loanAmount } = currentSubmission || {}

  const loanValue =
    loanType === PURCHASE_TYPE
      ? extractNumber(estimatedValue) - extractNumber(downPayment)
      : extractNumber(loanAmount)

  const isDiscountPositive = extractNumber(discount) > 0
  const discountValue = (Math.abs(extractNumber(discount)) / 100) * loanValue
  const discountDisplayedValueDesktop = isDiscountPositive
    ? `incl. ${currency(discountValue)} Points`
    : `incl. -${currency(discountValue)} Credits`
  const discountDisplayedValueMobile = isDiscountPositive
    ? `Points: ${currency(discountValue)}`
    : `Credits: -${currency(discountValue)}`

  return isMobile ? discountDisplayedValueMobile : discountDisplayedValueDesktop
}

const ProductTile = ({
  formData,
  product: {
    discount,
    uuid,
    externalProductId,
    lender: { name, slug, nmls },
    monthlyPayment,
    apr,
    rate,
    fees,
    loanTerm,
    amortizationType,
  },
  onGetStarted,
  dashboardContexts,
  isMultiVertical,
  styles: defaultStylesProps,
  trackSnowplowEvent,
  multiVerticalVariation,
  submission,
  loading,
}) => {
  const [isOpen, setOpen] = useState(false)
  const styles = isMultiVertical ? multiVerticalStyles : defaultStylesProps
  const fontTypes = getFontTypesFromMultivertical(isMultiVertical)
  const iconClass = isOpen ? 'icon-chevron-up' : 'icon-chevron-down'
  const isDisclaimer = lendersWithDisclaimerList.includes(name)
  const isShortenedMultivertical = multiVerticalVariation === 'shortened'

  const toggleLenderDisclaimer = () => {
    setOpen(!isOpen)
    trackSnowplowEvent(collapsibleClick, {
      text: 'Disclaimers',
      action: isOpen ? 'collapse' : 'expand',
    })
  }

  return (
    <Testable
      className={cx(
        styles.container,
        isDisclaimer && styles.flexWrap,
        loading && styles.loadingContainer,
      )}
      key={uuid}
      testId="product"
      id={externalProductId}
      data-test-id={`rateTableTile_${slug}`}
    >
      <div className={`${styles.firstRow} ${isDisclaimer ? styles.marginBottomZero : ''}`}>
        <div className={cx(styles.lenderSection)}>
          <div className={cx(loading && styles.loadingBlock)}>
            <img
              className={cx(styles.itemLenderLogo)}
              src={lenderDashboardImages(slug)}
              alt={name}
            />
          </div>
          <p
            className={cx(
              'disclaimer neutral500 mt-4',
              styles.nmlsText,
              loading && styles.loadingBlock,
            )}
          >
            NMLS #{nmls}
          </p>
          {!isMultiVertical && (
            <Testable testId="loan-type" className={styles.displayDesktop}>
              {amortizationType === 'Fixed' && (
                <p className={cx('disclaimer semiBold', loading && styles.loadingBlock)}>
                  {loanTerm}-year fixed
                </p>
              )}
              {amortizationType === 'ARM' && (
                <p className={cx('disclaimer', loading && styles.loadingBlock)}>5/1 adjustable</p>
              )}
            </Testable>
          )}

          {isDisclaimer &&
            (isShortenedMultivertical ? (
              <DisclosureModal title="Disclaimers" name={`${name}-disclaimer`}>
                <p className="disclaimer">{disclaimerContents[name]}</p>
              </DisclosureModal>
            ) : (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <p
                onClick={toggleLenderDisclaimer}
                className={cx(styles.disclaimersLink, 'neutral500')}
              >
                Disclaimers <span className={`cred-icon ${iconClass}`} />
              </p>
            ))}
        </div>
        {!isMultiVertical && (
          <Testable testId="fees-discount" className={styles.displayMdWidth}>
            <p
              className={cx(
                'disclaimer semiBold',
                styles.displayForMdWidth,
                loading && styles.loadingBlock,
              )}
            >
              {getDiscountDisplayValue(discount, { formData, submission })}
            </p>
          </Testable>
        )}
        {isMultiVertical && (
          <div className={`${styles.buttonSectionMobile}`}>
            <CtaButton
              className={cx(
                styles.ctaMobile,
                styles.submitButtonLoadingBlock,
                loading && styles.loadingBlock,
                loading && styles.loadingButton,
              )}
              onClick={onGetStarted}
              analyticsContexts={[
                ...dashboardContexts,
                spProduct.analytics({
                  loanProductId: `${externalProductId}`,
                  eligibilityProductId: '0',
                }),
              ]}
            >
              Get Started
            </CtaButton>
            <p
              className={cx(
                `mt-8 disclaimer neutral500`,
                styles.ctaModifierMobile,
                loading && styles.loadingBlock,
              )}
            >
              on Credible’s website
            </p>
          </div>
        )}
      </div>

      {!isMultiVertical && (
        <>
          <Testable className={styles.secondRowDesktop} testId="product-details">
            <Testable className={styles.itemSection} testId="rate-details">
              <Testable testId="rate">
                <p
                  className={cx(
                    'medium bold',
                    styles.arapeyFontFamily,
                    loading && styles.loadingBlock,
                  )}
                >
                  {number(rate || 0, 3)}%
                </p>
                <div className={styles.rateContent}>
                  <p className={cx('neutral500', fontTypes.medium, loading && styles.loadingBlock)}>
                    Rate
                  </p>
                </div>
              </Testable>
            </Testable>
            <Testable className={styles.itemSection} testId="apr-details">
              <Testable testId="amount">
                <p
                  className={cx(
                    'medium bold',
                    styles.arapeyFontFamily,
                    loading && styles.loadingBlock,
                  )}
                >
                  {number(apr || 0, 3)}%
                </p>
              </Testable>
              <div className={styles.aprContent}>
                <p className={cx('neutral500', fontTypes.medium, loading && styles.loadingBlock)}>
                  APR
                </p>
                <Tooltip
                  triggerContent={
                    <i
                      className={cx(
                        `cred-icon icon-help-outline`,
                        styles.icon,
                        loading && styles.loadingBlock,
                      )}
                    />
                  }
                  placement="top"
                  className={cx(styles.tooltip)}
                  contentClassName={styles.contentStyles}
                  containerClassName={styles.containerStyles}
                  triggerClassName={styles.triggerWrapper}
                  arrowClassName={styles.arrowClassName}
                  analyticsOptions={{ contentSubject: 'Annual Percentage Rate Tooltip' }}
                >
                  <p className="neutral500">{APT_TOOLTIP_TEXT}</p>
                </Tooltip>
              </div>
            </Testable>
            <Testable className={styles.itemSection} testId="monthly-payment-details">
              <Testable testId="amount">
                <p
                  className={cx(
                    'medium bold',
                    styles.arapeyFontFamily,
                    loading && styles.loadingBlock,
                  )}
                >
                  {currency(monthlyPayment)}
                </p>
              </Testable>
              <Testable testId="title">
                <p className={cx('neutral500', fontTypes.medium, loading && styles.loadingBlock)}>
                  Mo. payment
                </p>
              </Testable>
            </Testable>
            <Testable className={styles.itemSection} testId="fees-details">
              <Testable testId="amount">
                <p
                  className={cx(
                    'medium bold',
                    styles.arapeyFontFamily,
                    loading && styles.loadingBlock,
                  )}
                >
                  {currency((fees < 0 ? 0 : fees) || 0)}
                </p>
              </Testable>
              <Testable testId="title">
                <p className={cx('neutral500', fontTypes.medium, loading && styles.loadingBlock)}>
                  Fees
                </p>
              </Testable>
              <Testable testId="fees-discount" className={styles.displayForLgWidth}>
                <p className={cx('disclaimer semiBold', loading && styles.loadingBlock)}>
                  {getDiscountDisplayValue(discount, { formData, submission })}
                </p>
              </Testable>
            </Testable>
            <div className={styles.buttonSectionDesktop}>
              <CtaButton
                className={cx(
                  !isMultiVertical && styles.ctaDesktop,
                  loading && styles.loadingBlock,
                )}
                size={isMultiVertical ? 'small' : 'regular'}
                onClick={onGetStarted}
                analyticsContexts={[
                  ...dashboardContexts,
                  spProduct.analytics({
                    loanProductId: `${externalProductId}`,
                    eligibilityProductId: '0',
                  }),
                  spContext.analytics({ name: 'Product tile', description: `${name}, ${rate}` }),
                ]}
              >
                Get Started
              </CtaButton>
              <p
                className={cx(
                  'disclaimer neutral500 mt-8',
                  styles.ctaModifierTablet,
                  loading && styles.loadingBlock,
                )}
              >
                on Credible’s website
              </p>
            </div>
          </Testable>
          <Testable className={styles.secondRowMobile} testId="product-details">
            <Testable className={styles.itemSectionMobile} testId="rate-details">
              <Testable testId="rate">
                <p
                  className={cx(
                    'medium bold',
                    styles.arapeyFontFamily,
                    loading && styles.loadingBlock,
                  )}
                >
                  {number(rate || 0, 3)}%
                </p>
                <div className={styles.rateContent}>
                  <p className={cx('neutral500', fontTypes.medium, loading && styles.loadingBlock)}>
                    Rate
                  </p>
                </div>
              </Testable>
            </Testable>
            <Testable className={styles.itemSectionMobile} testId="apr-details">
              <Testable testId="amount">
                <p
                  className={cx(
                    'medium bold',
                    styles.arapeyFontFamily,
                    loading && styles.loadingBlock,
                  )}
                >
                  {number(apr || 0, 3)}%
                </p>
              </Testable>
              <div className={styles.aprContent}>
                <p className={cx('neutral500', fontTypes.medium, loading && styles.loadingBlock)}>
                  APR
                </p>
                <Tooltip
                  triggerContent={
                    <i
                      className={cx(
                        `cred-icon icon-help-outline`,
                        styles.icon,
                        loading && styles.loadingBlock,
                      )}
                    />
                  }
                  placement="top"
                  className={cx(styles.tooltip)}
                  contentClassName={styles.contentStyles}
                  containerClassName={styles.containerStyles}
                  triggerClassName={styles.triggerWrapper}
                  arrowClassName={styles.arrowClassName}
                  analyticsOptions={{ contentSubject: 'Annual Percentage Rate Tooltip' }}
                >
                  <p className="neutral500">{APT_TOOLTIP_TEXT}</p>
                </Tooltip>
              </div>
            </Testable>
          </Testable>
          <Testable className={styles.thirdRowMobile}>
            <Testable className={styles.itemSectionMobile} testId="monthly-payment-details">
              <Testable testId="amount">
                <p
                  className={cx(
                    'bodySmall bold',
                    styles.arapeyFontFamily,
                    styles.displayMobile,
                    loading && styles.loadingBlock,
                  )}
                >
                  {currency(monthlyPayment)}
                </p>
              </Testable>
              <Testable testId="title">
                <p
                  className={cx(
                    'bodySmall neutral500',
                    styles.displayMobile,
                    loading && styles.loadingBlock,
                  )}
                >
                  Mo. payment
                </p>
              </Testable>
            </Testable>
            <Testable className={styles.itemSectionMobile} testId="fees-details">
              <Testable testId="amount">
                <p
                  className={cx(
                    'bodySmall bold',
                    styles.arapeyFontFamily,
                    styles.displayMobile,
                    loading && styles.loadingBlock,
                  )}
                >
                  {currency((fees < 0 ? 0 : fees) || 0)}
                </p>
              </Testable>
              <Testable testId="title">
                <p
                  className={cx(
                    'bodySmall neutral500',
                    styles.displayMobile,
                    loading && styles.loadingBlock,
                  )}
                >
                  Fees
                </p>
              </Testable>
            </Testable>
            <Testable className={styles.itemSectionMobile}>
              <Testable testId="loanTerm">
                {!isMultiVertical && (
                  <Testable testId="loan-type" className={styles.displayMobile}>
                    {amortizationType === 'Fixed' && (
                      <p className={cx('bodySmall bold', loading && styles.loadingBlock)}>
                        {loanTerm}-year fixed
                      </p>
                    )}
                    {amortizationType === 'ARM' && (
                      <p className={cx('bodySmall', loading && styles.loadingBlock)}>
                        5/1 adjustable
                      </p>
                    )}
                  </Testable>
                )}
                <Testable testId="title" className={styles.displayMobile}>
                  <p className={cx('bodySmall neutral500', loading && styles.loadingBlock)}>
                    Loan term
                  </p>
                </Testable>
              </Testable>
            </Testable>
          </Testable>
          <Testable testId="mortgage-combined-product-tile-cta">
            <div className={`${styles.buttonSectionMobile}`}>
              <CtaButton
                className={cx(
                  styles.ctaMobile,
                  styles.submitButtonLoadingBlock,
                  loading && styles.loadingBlock,
                )}
                onClick={onGetStarted}
                analyticsContexts={[
                  ...dashboardContexts,
                  spProduct.analytics({
                    loanProductId: `${externalProductId}`,
                    eligibilityProductId: '0',
                  }),
                ]}
              >
                Get Started
              </CtaButton>
              <p
                className={cx(
                  `disclaimer neutral500 mt-8`,
                  styles.ctaModifierMobile,
                  loading && styles.loadingBlock,
                )}
              >
                on Credible’s website
              </p>
            </div>
          </Testable>
        </>
      )}

      {isMultiVertical && (
        <Testable className={styles.secondRow} testId="product-details">
          <Testable className={styles.itemSection} testId="monthly-payment-details">
            <p
              className={cx('neutral500', fontTypes.medium, onlyMobile, {
                [styles.loadingBlock]: loading,
              })}
            >
              Mo. payment
            </p>
            <p
              className={cx('neutral500', fontTypes.medium, exceptMobile, {
                [styles.loadingBlock]: loading,
              })}
            >
              Monthly payment
            </p>
            <Testable testId="amount">
              <p
                className={cx('semiBold', fontTypes.large, styles.arapeyFontFamily, {
                  [styles.loadingBlock]: loading,
                })}
              >
                {currency(monthlyPayment)}
              </p>
            </Testable>
            <Testable testId="loan-type">
              {amortizationType === 'Fixed' && (
                <p
                  className={cx('disclaimer secondary500', {
                    semiBold: isMultiVertical,
                    [styles.loadingBlock]: loading,
                  })}
                >
                  {loanTerm}-year fixed 1
                </p>
              )}
              {amortizationType === 'ARM' && (
                <p
                  className={cx('disclaimer secondary500', {
                    semiBold: isMultiVertical,
                    [styles.loadingBlock]: loading,
                  })}
                >
                  5/1 adjustable
                </p>
              )}
            </Testable>
          </Testable>
          <Testable className={styles.itemSection} testId="apr-details">
            <div className={cx(styles.aprContent, { [styles.loadingBlock]: loading })}>
              <p className={cx('neutral500', fontTypes.medium, { [styles.loadingBlock]: loading })}>
                APR
              </p>
              <Tooltip
                triggerContent={<i className={`cred-icon icon-help-outline ${styles.icon}`} />}
                placement="top"
                className={styles.tooltip}
                contentClassName={styles.contentStyles}
                containerClassName={styles.containerStyles}
                triggerClassName={styles.triggerWrapper}
                arrowClassName={styles.arrowClassName}
                analyticsOptions={{ contentSubject: 'Annual Percentage Rate Tooltip' }}
              >
                <p className="neutral500">{APT_TOOLTIP_TEXT}</p>
              </Tooltip>
            </div>
            <Testable className={cx({ [styles.loadingBlock]: loading })} testId="amount">
              <p className={cx('semiBold', fontTypes.large, styles.arapeyFontFamily)}>
                {number(apr || 0, 3)}%
              </p>
            </Testable>
            <Testable className={cx({ [styles.loadingBlock]: loading })} testId="rate-type">
              <p className={cx('disclaimer', { semiBold: isMultiVertical })}>
                {number(rate || 0, 3)}% Rate
              </p>
            </Testable>
          </Testable>
          <Testable className={styles.itemSection} testId="fees-details">
            <p className={cx('neutral500', fontTypes.medium, { [styles.loadingBlock]: loading })}>
              Fees
            </p>
            <Testable testId="amount">
              <p
                className={cx('semiBold', fontTypes.large, styles.arapeyFontFamily, {
                  [styles.loadingBlock]: loading,
                })}
              >
                {currency((fees < 0 ? 0 : fees) || 0)}
              </p>
            </Testable>
            <Testable testId="fees-discount">
              <p
                className={cx('disclaimer secondary500', {
                  semiBold: isMultiVertical,
                  [styles.loadingBlock]: loading,
                })}
              >
                {getDiscountDisplayValue(discount, { formData, submission })}
              </p>
            </Testable>
          </Testable>
          <div className={styles.buttonSectionDesktop}>
            <CtaButton
              className={cx({ [styles.loadingBlock]: loading, [styles.loadingButton]: loading })}
              size={isMultiVertical ? 'small' : 'regular'}
              onClick={onGetStarted}
              analyticsContexts={[
                ...dashboardContexts,
                spProduct.analytics({
                  loanProductId: `${externalProductId}`,
                  eligibilityProductId: '0',
                }),
                spContext.analytics({ name: 'Product tile', description: `${name}, ${rate}` }),
              ]}
            >
              Get Started
            </CtaButton>
            <p
              className={cx('disclaimer neutral500 mt-8', styles.ctaModifierTablet, {
                [styles.loadingBlock]: loading,
              })}
            >
              on Credible’s website
            </p>
          </div>
        </Testable>
      )}
      {isDisclaimer && (
        <DisclaimerContent
          className={cx(styles.thirdRow, {
            [styles.displayNone]: !isOpen,
            [styles.loadingBlock]: loading && isMultiVertical,
          })}
          isOpen={isOpen}
          lenderName={name}
        />
      )}
    </Testable>
  )
}

ProductTile.propTypes = {
  formData: PropTypes.object.isRequired,
  product: PropTypes.object.isRequired,
  onGetStarted: PropTypes.func.isRequired,
  dashboardContexts: PropTypes.array.isRequired,
  isMultiVertical: PropTypes.bool,
  styles: PropTypes.object,
  trackSnowplowEvent: PropTypes.func,
  multiVerticalVariation: PropTypes.string,
  submission: PropTypes.shape({
    downPayment: PropTypes.number,
    estimatedValue: PropTypes.number,
    loanAmount: PropTypes.number,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
}

const mapStateToProps = ({ submission }) => ({ submission })

export default compose(
  withSnowplowContexts,
  connect(mapStateToProps),
  withStyles(defaultStyles),
)(ProductTile)
